import {
  HOSTED_THEN_MONTHLY,
  HOSTED_THEN_MONTHLY_LABEL,
  HOSTED_YEAR_ONE,
  HOSTED_YEAR_ONE_LABEL,
  PLAN_LABEL_FALLBACK,
} from '../../helpers/hostedOffer';

// Display copy only. Charged amounts come from the subscription returned by
// the API, not from these labels.
export const PRICING = {
  yearOne: { label: HOSTED_YEAR_ONE, display: HOSTED_YEAR_ONE_LABEL },
  monthly: { label: HOSTED_THEN_MONTHLY, display: HOSTED_THEN_MONTHLY_LABEL },
};

export const statusToUi = (status) => {
  switch (status) {
    case 'active':
      return 'live';
    case 'trial':
      return 'staging';
    case 'provisioning':
      return 'provisioning';
    case 'suspended':
    case 'disabled':
      return 'paused';
    default:
      return status || 'staging';
  }
};

export const statusLabel = (status) => {
  const uiStatus = statusToUi(status);
  switch (uiStatus) {
    case 'live':
      return 'Live';
    case 'staging':
      return 'Staging';
    case 'provisioning':
      return 'Provisioning';
    case 'paused':
      return 'Paused';
    default:
      return 'Staging';
  }
};

export const billingLabelForSite = (site) => {
  const uiStatus = statusToUi(site?.status);
  if (uiStatus === 'live') {
    const subscription = Array.isArray(site?.subscriptions) ? site.subscriptions[0] : null;
    if (subscription?.amount) {
      const amount = `$${(subscription.amount / 100).toFixed(0)}`;
      return subscription.billing_interval === 'yearly' ? `${amount} / yr` : `${amount} / mo`;
    }
    return PLAN_LABEL_FALLBACK;
  }

  return 'Free until published';
};

export const siteName = (site) => site?.site_title || site?.name || 'Untitled site';

export const siteDomain = (site) =>
  site?.custom_domain || site?.domain || site?.url || 'staging site pending';

export const paletteForSite = (site) => {
  const palettes = ['warm', 'sage', 'violet', 'slate', 'blue'];
  const source = `${site?.id || siteDomain(site) || siteName(site)}`;
  const total = source.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return palettes[total % palettes.length];
};

export const userInitials = (user) => {
  const first = user?.first_name || user?.firstName || '';
  const last = user?.last_name || user?.lastName || '';
  const email = user?.email || '';
  const fallback = email.slice(0, 2);
  return `${first[0] || fallback[0] || 'N'}${last[0] || fallback[1] || 'C'}`.toUpperCase();
};

export const userFullName = (user) => {
  const full = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
  return full || user?.email || 'North Commerce';
};

export const formatCurrency = (amount, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((amount || 0) / 100);

export const formatDate = (date) => {
  if (!date) return 'Pending';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date));
};

// A freshly created site keeps provisioning (SSL, plugins) for a few minutes
// after the DB row exists. We show a countdown for this long after
// provisioned_at/created_at. SSL issuance usually lands in 2-4 minutes.
export const PROVISION_WINDOW_MS = 4 * 60 * 1000;

// Milliseconds left in the provisioning window for a site, or 0 if past it.
export const provisioningMsLeft = (site) => {
  const start = site?.provisioned_at || site?.created_at;
  if (!start) return 0;
  const left = PROVISION_WINDOW_MS - (Date.now() - new Date(start).getTime());
  return left > 0 ? left : 0;
};

// Whole days remaining until `date` (ISO string). Returns null for missing /
// unparseable input so callers can degrade gracefully instead of showing NaN.
// Clamps at 0 — a past date reads as "0 days".
export const daysUntil = (date) => {
  if (!date) return null;
  const target = new Date(date).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
};
